import { yupResolver } from '@hookform/resolvers/yup';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { useLocaleText } from '@mui/x-date-pickers/internals';
import { PickersActionBarProps } from '@mui/x-date-pickers/PickersActionBar';
import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { EndorsementConfigurationResponse } from '../../../api/ConfigApi';
import EndorsementsApi from '../../../api/EndorsementsApi';
import { handleUnauthorizedResponse } from '../../../api/http';
import {
	AutofillOperations,
	AutofillOperationTypes,
} from '../../../api/IntegrationsApi';
import {
	ENDORSEMENT_POLICY_END_DATE_ENABLED,
	SHOW_SECOND_PRECISION_ON_ENDORSEMENT_DATES_ENABLED,
	useAuthContext,
} from '../../../context/auth';
import { useConfigContext } from '../../../context/config';
import { useErrorContext } from '../../../context/ErrorProvider';
import { usePolicyContext } from '../../../context/policy';
import {
	ENDORSEMENT_CREATE_FAILURE,
	ENDORSEMENT_NEW_POLICY_END_DATE_WARNING_MSG,
} from '../../../data/labelsAndMessages';
import { dayjs } from '../../../date-util';
import { EndorsementCreateRequest } from '../../../types/endorsements';
import { PolicyCharacteristics } from '../../../types/policy';
import {
	getActiveCharacteristics,
	isAutofillConfigured,
} from '../../../util/util';
import DropdownButtonItem from '../../DropdownButtonItem';
import {
	DATE_FIELD_VALIDATIONS,
	DATE_PRECISION,
	DATE_PRECISION_BACKEND_FORMAT,
	DATE_PRECISION_INPUT_FORMAT,
	FORM_MESSAGE,
} from '../../fastForm/data/constants';
import { DateField } from '../../fastForm/fields';
import { dateConversion } from '../../fastForm/utils';
import { errorToast } from '../../Toast';

interface Props {
	premiumReportEndDates: string[];
	endorsement: EndorsementConfigurationResponse;
	openOverride?: boolean;
}

interface FormValues {
	effectiveDate: string;
	newPolicyEndDate: string;
}

const EndorsementCreateTrigger = (props: Props) => {
	const authData = useAuthContext();
	const policyData = usePolicyContext();
	const configData = useConfigContext();
	const errorData = useErrorContext();
	const navigate = useNavigate();
	const timezone = authData.auth.timezone;
	const { policy, productConfig } = policyData;
	const { endorsement, premiumReportEndDates, openOverride } = props;
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const [open, setIsOpen] = useState(openOverride || false);
	const { effectiveContractEndTimestamp, characteristics } = policy;
	const { policyStartTimestamp } = (
		getActiveCharacteristics(characteristics) as PolicyCharacteristics[]
	)[0];

	const isSecondPrecisionEnabled =
		authData.auth.configProperties[
			SHOW_SECOND_PRECISION_ON_ENDORSEMENT_DATES_ENABLED
		];

	const isEndorsementEndDateEnabled =
		authData.auth.configProperties[ENDORSEMENT_POLICY_END_DATE_ENABLED];

	const DATE_PICKER_PRECISION = isSecondPrecisionEnabled
		? DATE_PRECISION.SECOND
		: DATE_PRECISION.DAY;

	const endorsementConfiguration =
		productConfig.policyConfiguration.endorsements.filter(
			(e) => e.name === endorsement.name,
		);

	const minimumEffectiveDate = dateConversion.convertTimestampToDayJS(
		premiumReportEndDates.length > 0
			? premiumReportEndDates.sort().pop()
			: policyStartTimestamp,
		timezone,
	);

	const minimumEffectiveDateString = minimumEffectiveDate.format(
		DATE_PRECISION_BACKEND_FORMAT[DATE_PICKER_PRECISION],
	);

	const maximumEffectiveDate = dateConversion.convertTimestampToDayJS(
		effectiveContractEndTimestamp,
		timezone,
	);

	const maximumEffectiveDateString = maximumEffectiveDate.format(
		DATE_PRECISION_BACKEND_FORMAT[DATE_PICKER_PRECISION],
	);

	const minimumEffectiveDateInputString = minimumEffectiveDate.format(
		DATE_PRECISION_INPUT_FORMAT[DATE_PICKER_PRECISION],
	);

	const maximumEffectiveDateInputString = maximumEffectiveDate.format(
		DATE_PRECISION_INPUT_FORMAT[DATE_PICKER_PRECISION],
	);

	const currentEndDate = maximumEffectiveDate.format(
		DATE_PRECISION_INPUT_FORMAT[DATE_PICKER_PRECISION],
	);
	const helperText = `Current end date: ${currentEndDate}`;

	const handleOpen = () => {
		setIsOpen(true);
	};

	const handleClose = () => {
		setIsOpen(false);
	};

	const minimumEffectiveDateErrorString =
		DATE_FIELD_VALIDATIONS.MIN_DATE_ERROR_MESSAGE +
		minimumEffectiveDateInputString;

	const maximumEffectiveDateErrorString =
		DATE_FIELD_VALIDATIONS.MAX_DATE_ERROR_MESSAGE +
		maximumEffectiveDateInputString;

	const invalidDateErrorString =
		DATE_FIELD_VALIDATIONS.INVALID_DATE_ERROR_MESSAGE;

	const { control, handleSubmit, watch, setValue } = useForm({
		defaultValues: {
			effectiveDate: '',
			newPolicyEndDate: '',
		},
		resolver: yupResolver(
			yup.object().shape({
				effectiveDate: yup
					.string()
					.required(FORM_MESSAGE.required)
					.test('is-valid-date', invalidDateErrorString, (value) => {
						const date = dateConversion.dateFormStringToDayJs(
							value,
							DATE_PICKER_PRECISION,
						);
						return date?.isValid();
					})
					.test(
						'is-after-minimum-date',
						minimumEffectiveDateErrorString,
						(value) => {
							const currentDate =
								dateConversion.convertDateStringToZonedTimestamp(
									value,
									DATE_PICKER_PRECISION,
									timezone,
								);
							const minimumDate =
								dateConversion.convertDateStringToZonedTimestamp(
									minimumEffectiveDateInputString,
									DATE_PICKER_PRECISION,
									timezone,
								);
							return parseInt(currentDate) >= parseInt(minimumDate);
						},
					)
					.test(
						'is-before-maximum-date',
						maximumEffectiveDateErrorString,
						(value) => {
							const currentDate =
								dateConversion.convertDateStringToZonedTimestamp(
									value,
									DATE_PICKER_PRECISION,
									timezone,
								);
							const maximumDate =
								dateConversion.convertDateStringToZonedTimestamp(
									maximumEffectiveDateInputString,
									DATE_PICKER_PRECISION,
									timezone,
								);
							return parseInt(currentDate) <= parseInt(maximumDate);
						},
					),
				newPolicyEndDate: yup
					.string()
					.test('is-valid-date', invalidDateErrorString, (value) => {
						if (value === '') {
							return true;
						}
						const date = dateConversion.dateFormStringToDayJs(
							value,
							DATE_PICKER_PRECISION,
						);
						return date?.isValid();
					})
					.test(
						'is-at-midnight',
						'New Policy End Date must be at exactly midnight',
						(value) => {
							if (value === '') {
								return true;
							}
							const date = dateConversion.dateFormStringToDayJs(
								value,
								DATE_PICKER_PRECISION,
							);
							if (
								date.hour() === 0 &&
								date.minute() === 0 &&
								date.second() === 0
							) {
								return true;
							}
							return false;
						},
					)
					.when('effectiveDate', (effectiveDate: string, schema: any) => {
						return schema.test(
							'is-after-effective-date',
							`New policy end date must be after ${
								effectiveDate ? effectiveDate : 'effective date'
							}`,
							(value: string) => {
								if (value === '') {
									return true;
								}
								const effectiveDateObject =
									dateConversion.dateFormStringToDayJs(
										effectiveDate,
										DATE_PICKER_PRECISION,
									);
								const newPolicyEndDateObject =
									dateConversion.dateFormStringToDayJs(
										value,
										DATE_PICKER_PRECISION,
									);
								return newPolicyEndDateObject > effectiveDateObject;
							},
						);
					}),
			}),
		),
	});

	const currEffectiveDateTimeString = watch('effectiveDate');
	const currNewPolicyEndDateTimeString = watch('newPolicyEndDate');

	const currEffectiveDayJsObj = dateConversion.dateFormStringToDayJs(
		currEffectiveDateTimeString,
		DATE_PICKER_PRECISION,
	);

	const onSubmit: SubmitHandler<FormValues> = async (data) => {
		setLoading(true);
		const zonedEffectiveDateTimestamp =
			dateConversion.convertDateStringToZonedTimestamp(
				data.effectiveDate,
				DATE_PICKER_PRECISION,
				timezone,
			);

		const zonedNewPolicyEndDateTimestamp =
			dateConversion.convertDateStringToZonedTimestamp(
				data.newPolicyEndDate,
				DATE_PICKER_PRECISION,
				timezone,
			);

		const reprice =
			endorsementConfiguration[0] &&
			endorsementConfiguration[0].reprice === 'input'
				? true
				: false;

		const autofillEnabled = isAutofillConfigured(
			policy.productName,
			AutofillOperations.ENDORSEMENT,
			AutofillOperationTypes.CREATE,
			configData?.dataAutofillSequences ?? [],
		);

		const endorsementParams: EndorsementCreateRequest = {
			endorsementName: endorsement.name,
			startTimestamp: zonedEffectiveDateTimestamp,
			addExposures: [],
			addFieldGroups: [],
			endExposures: [],
			fieldValues: {},
			removeFieldGroups: [],
			updateExposures: [],
			updateFieldGroups: [],
			// default specified autofill sequence will run when autofill array is null
			autofill: autofillEnabled ? null : [],
		};

		if (zonedNewPolicyEndDateTimestamp !== '') {
			endorsementParams.newPolicyEndTimestamp = parseInt(
				zonedNewPolicyEndDateTimestamp,
			);
		}

		if (reprice) {
			endorsementParams.reprice = reprice;
		}

		try {
			const endorsementResponse = await EndorsementsApi.createEndorsement(
				authData.auth.authorizationToken,
				policy.locator,
				endorsementParams,
			);

			navigate(`endorsements/${endorsementResponse.data.locator}`);
		} catch (error) {
			setLoading(false);
			errorToast(ENDORSEMENT_CREATE_FAILURE());
			errorData.handleErrorResponse(error, ENDORSEMENT_CREATE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
	};

	const handleModalClose = (
		reason: 'backdropClick' | 'escapeKeyDown',
		callback: () => void,
	) => {
		if (reason !== 'backdropClick') {
			callback();
		}
	};

	const EffectiveDateActionBar = (props: PickersActionBarProps) => {
		const localeText = useLocaleText();

		const menuItems = [
			<Button
				type='button'
				data-mui-test='today-action-button'
				onClick={() => {
					setValue(
						'effectiveDate',
						dayjs()
							.startOf('day')
							.format(DATE_PRECISION_INPUT_FORMAT[DATE_PICKER_PRECISION]),
					);
				}}
				key={'today'}
			>
				{localeText.todayButtonLabel}
			</Button>,
			<Button
				type='button'
				onClick={() => {
					setValue('effectiveDate', minimumEffectiveDateInputString);
				}}
				key={'accept'}
			>
				Policy start date
			</Button>,
		];

		return <DialogActions>{menuItems}</DialogActions>;
	};

	return (
		<>
			<DropdownButtonItem
				onClick={handleOpen}
				label={endorsement.displayName}
			/>
			<Dialog
				fullWidth
				open={open}
				onClose={(e, r) => handleModalClose(r, handleClose)}
				aria-labelledby='add-endorsement-modal'
			>
				<Box component='form' onSubmit={handleSubmit(onSubmit)}>
					<DialogTitle>
						{`${t('endorsement.singular.upper')} Dates`}
					</DialogTitle>
					<DialogContent>
						<Stack spacing={2} sx={{ width: '100%' }}>
							<DateField<FormValues>
								disableTimePicker
								CustomActionBar={EffectiveDateActionBar}
								authData={authData}
								control={control}
								showCalendar
								minimum={minimumEffectiveDateString}
								maximum={maximumEffectiveDateString}
								name='effectiveDate'
								title='Effective Date'
								precision={
									DATE_PICKER_PRECISION as 'second' | 'day' | 'month' | 'year'
								}
								type='date'
								values={[]}
								order={0}
							/>
							{isEndorsementEndDateEnabled && (
								<DateField<FormValues>
									disableTimePicker
									authData={authData}
									control={control}
									optional
									helperText={helperText}
									showCalendar
									defaultCalendarMonth={
										currEffectiveDayJsObj.isValid()
											? currEffectiveDayJsObj.add(1, 'year')
											: null
									}
									minimum={
										currEffectiveDateTimeString
											? currEffectiveDateTimeString
											: null
									}
									name='newPolicyEndDate'
									title='New Policy End Date'
									precision={
										DATE_PICKER_PRECISION as 'second' | 'day' | 'month' | 'year'
									}
									type='date'
									values={[]}
									order={0}
								/>
							)}
							{!!currNewPolicyEndDateTimeString && (
								<Alert severity='warning'>
									{ENDORSEMENT_NEW_POLICY_END_DATE_WARNING_MSG()}
								</Alert>
							)}
						</Stack>
					</DialogContent>
					<DialogActions sx={{ m: 2 }}>
						<Button
							onClick={handleClose}
							color='neutralButton'
							disabled={loading}
						>
							Close
						</Button>
						<Button
							color='secondary'
							variant='contained'
							type='submit'
							disabled={loading}
						>
							Confirm
						</Button>
					</DialogActions>
				</Box>
			</Dialog>
		</>
	);
};

export default EndorsementCreateTrigger;
